import { ReactElement } from 'react';
import { Stack } from '@mui/material';
import {
  User, ZoomSettings, PresetLocations,
} from '../../../store';
import LocationsContainer from './Locations';
import {
  SchedulingPreferencesList, SchedulingPreferences
} from '../../../store/cabinetApi/generated/scheduling';
import { MeetingDefaults } from './MeetingDefaults';
import { CalendarDefaults } from './CalendarDefaults';
import { SchedulingNotificationsTab } from './SchedulingNotificationsTab';
import { SavedTemplatesTab } from './SavedTemplatesTab';

export interface Props {
  user: User | null | undefined;
  schedulingPrefs: SchedulingPreferencesList | undefined;
  onSchedulingPrefChange: (pref: Partial<SchedulingPreferences>) => void;
  onUpdateDefaultSecondaryTz: (idx: number, newTz: string | null) => void;
  onAddDefaultSecondaryTz: () => void;
  onRemoveDefaultSecondaryTz: (idx: number) => void;
  openUpgradeModal: () => void;
  presetLocations: PresetLocations;
  zoomSettings: {
    [settingsId: string]: ZoomSettings;
  };
  onAddZoom: () => void;
  hasMicrosoftGrant: boolean;
  hasGoogleGrant: boolean;
  /** URL segment under Scheduling settings, e.g. `calendar-defaults` */
  schedulingSubTab?: string;
}

export const UserPreferences = ({
  user, schedulingPrefs, onSchedulingPrefChange, openUpgradeModal, onUpdateDefaultSecondaryTz,
  onAddDefaultSecondaryTz, onRemoveDefaultSecondaryTz, presetLocations, zoomSettings, onAddZoom,
  hasMicrosoftGrant, hasGoogleGrant, schedulingSubTab,
}: Props): ReactElement | null => {
  if (!user || !schedulingPrefs) return null;

  const contentBySubTab: Record<string, ReactElement> = {
    'calendar-defaults': (
      <Stack
        sx={{
          marginLeft: 0,
          marginRight: 0,
          paddingBottom: 12,
          maxWidth: 900,
          gap: 2
        }}>
        <CalendarDefaults
          user={user}
          schedulingPrefs={schedulingPrefs}
          onSchedulingPrefChange={onSchedulingPrefChange}
          openUpgradeModal={openUpgradeModal}
          onUpdateDefaultSecondaryTz={onUpdateDefaultSecondaryTz}
          onAddDefaultSecondaryTz={onAddDefaultSecondaryTz}
          onRemoveDefaultSecondaryTz={onRemoveDefaultSecondaryTz}
        />
      </Stack>
    ),
    'custom-locations': (
      <Stack
        sx={{
          marginLeft: 0,
          marginRight: 0,
          paddingBottom: 12,
          maxWidth: 900,
          gap: 2
        }}>
        <LocationsContainer />
      </Stack>
    ),
    'saved-templates': (
      <Stack
        sx={{
          marginLeft: 0,
          marginRight: 0,
          paddingBottom: 12,
          maxWidth: 900,
          gap: 2
        }}>
        <SavedTemplatesTab />
      </Stack>
    ),
    'notifications': (
      <Stack
        sx={{
          marginLeft: { xs: 2, lg: "auto" },
          marginRight: { xs: 2, lg: "auto" },
          paddingBottom: 12,
          maxWidth: 900,
          gap: 2
        }}>
        <SchedulingNotificationsTab
          schedulingPrefs={schedulingPrefs}
          onSchedulingPrefChange={onSchedulingPrefChange}
        />
      </Stack>
    ),
  };

  return contentBySubTab[schedulingSubTab || ''] || (
    <Stack
      sx={{
        marginLeft: 0,
        marginRight: 0,
        paddingBottom: 12,
        maxWidth: 900,
        gap: 2
      }}>
      <MeetingDefaults
        user={user}
        schedulingPrefs={schedulingPrefs}
        onSchedulingPrefChange={onSchedulingPrefChange}
        openUpgradeModal={openUpgradeModal}
        presetLocations={presetLocations}
        zoomSettings={zoomSettings}
        onAddZoom={onAddZoom}
        hasMicrosoftGrant={hasMicrosoftGrant}
        hasGoogleGrant={hasGoogleGrant}
      />
    </Stack>
  );
};

export default UserPreferences;
